import logger from './logger';
import { checkoutSessionPaid } from './funnelCheckout';

/**
 * The browser's Google Ads subscription conversion — today the only report
 * Google Ads gets of a web purchase (the server-side upload in the backend's
 * integrations/google_ads_conversions.py is not configured in production).
 *
 * Every report is one Stripe Checkout Session our server has read back from
 * Stripe and seen paid, so:
 *   - `transaction_id` is the session id: Google keeps one conversion per id,
 *     so a second report of the same purchase (another tab, a later server
 *     upload keyed on the same session) is dropped instead of double counted
 *   - the value is what Stripe charged; for a free trial ($0 today) it is
 *     the plan's price, what they pay when the trial converts
 *   - a hand-typed `?upgraded=true` reports nothing
 */
export const SUBSCRIPTION_CONVERSION = 'AW-17614735089/yVTZCOHgyJ0cEPGNrs9B';

// Monthly list prices, for a free trial's value when the caller has no plan
// price of its own (the web paywall's return)
const MONTHLY_LIST_PRICES = { starter: 7.99, plus: 14.99, pro: 14.99, family: 24.99, unlimited: 49.99 };

export const listPrice = (tier, billing) => {
  const monthly = MONTHLY_LIST_PRICES[tier] || 14.99;
  return billing === 'annual' ? Math.round(monthly * 12 * 0.75 * 100) / 100 : monthly;
};

/**
 * "Started checkout", the funnel's begin_checkout. Sent as the GA4 standard
 * event (imported into Google Ads as a secondary key event, so Smart
 * Bidding has a signal that arrives hundreds of times a month, not a dozen)
 * and, once a Google Ads conversion label exists for it, as an Ads
 * conversion too. A value is the first charge, what the parent is about to
 * pay; no transaction id, this is not a purchase.
 */
export const BEGIN_CHECKOUT_CONVERSION = null; // 'AW-17614735089/<label>' once the action exists

export const reportBeginCheckout = ({ tier, billing, value, email }) => {
  if (typeof window === 'undefined' || !window.gtag) return false;
  const amount = Number(value) > 0 ? Math.round(Number(value) * 100) / 100 : listPrice(tier, billing);
  if (email) window.gtag('set', 'user_data', { email });
  window.gtag('event', 'begin_checkout', {
    currency: 'USD',
    value: amount,
    items: [{ item_id: `${tier || 'pro'}_${billing || 'monthly'}`, item_name: `Pixley ${tier || 'pro'} ${billing || 'monthly'}`, price: amount, quantity: 1 }],
  });
  if (BEGIN_CHECKOUT_CONVERSION) {
    window.gtag('event', 'conversion', { send_to: BEGIN_CHECKOUT_CONVERSION, value: amount, currency: 'USD' });
  }
  return true;
};

export const reportSubscriptionConversion = ({ sessionId, value, email }) => {
  if (!sessionId || typeof window === 'undefined' || !window.gtag) return false;
  if (email) window.gtag('set', 'user_data', { email });
  window.gtag('event', 'conversion', {
    send_to: SUBSCRIPTION_CONVERSION,
    value,
    currency: 'USD',
    transaction_id: sessionId,
  });
  return true;
};

/**
 * Hosted Stripe Checkout's return (`?upgraded=true&session_id=cs_…`): read the
 * session back (owner-only) and report it if paid. No session id, or a
 * session that isn't theirs or isn't paid, reports nothing.
 */
export async function reportCheckoutReturn(apiClient, { sessionId, tier, billing, email }) {
  if (!sessionId || !apiClient?.getCheckoutSession) return false;
  try {
    const res = await apiClient.getCheckoutSession(sessionId);
    const session = res?.status ? res.data : null;
    if (!checkoutSessionPaid(session)) return false;
    const charged = Number(session.amount_total) > 0 ? Number(session.amount_total) / 100 : null;
    const value = charged ?? listPrice(session.tier || tier, session.billing_period || billing);
    return reportSubscriptionConversion({ sessionId, value, email });
  } catch (error) {
    logger.warn('Google Ads conversion: checkout session unverified (not reported):', error?.name || 'Error');
    return false;
  }
}
